import React, { useEffect } from 'react';
import axios from 'axios';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

// Track click with a debounce mechanism to ensure only one click is registered
let clickTrackingInProgress = false;

// Generate a random click ID that will be used for this page load only
const generateClickId = () => {
  return `click_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// Store a unique click ID for this page load
const CLICK_ID = generateClickId();

// Check URL for referral code and store if found
export const captureReferralCode = (isLoggedIn = false) => {
  // Check if we've already tracked this session
  const hasTrackedThisSession = localStorage.getItem('hasTrackedReferral') === 'true';
  // Extract referral code from URL (/ref/CODE or ?ref=CODE formats)
  let referralCode = null;
  
  // Check for /ref/CODE format
  const pathname = window.location.pathname;
  // More robust path parsing with regex
  const refPathMatch = pathname.match(/\/ref\/([^\/]+)/i);
  if (refPathMatch && refPathMatch[1]) {
    referralCode = refPathMatch[1];
  }
  
  // Check for ?ref=CODE format as fallback
  if (!referralCode) {
    const urlParams = new URLSearchParams(window.location.search);
    referralCode = urlParams.get('ref');
  }
  
  // If referral code found, store it
  if (referralCode) {
    // Store the code for 30 days regardless of login state
    storeReferralCode(referralCode);
    
    // Only track click if user is not logged in and we haven't tracked this session
    // and no tracking is currently in progress
    if (!isLoggedIn && !hasTrackedThisSession && !clickTrackingInProgress) {
      trackReferralClickOnce(referralCode);
    } 
  }
  
  return referralCode;
};

// Track once per session with safeguards
const trackReferralClickOnce = (referralCode) => {
  // Set global flag to prevent multiple calls
  if (clickTrackingInProgress) {
    return;
  }
  
  // Additional check - if we've already tracked in localStorage (fast, synchronous check)
  if (localStorage.getItem('hasTrackedReferral') === 'true') {
    return;
  }
  
  // Set tracking flag
  clickTrackingInProgress = true;
  
  // Set tracked flag in localStorage immediately to prevent other components from trying
  localStorage.setItem('hasTrackedReferral', 'true');
  
  // Force the click tracking to happen immediately and ensure it completes
  trackReferralClick(referralCode, CLICK_ID)
    .then((response) => {
      // We've already set the localStorage flag above, but also do it here for consistency
      sessionStorage.setItem('hasTrackedReferral', 'true');
      localStorage.setItem('referralCodeLastTracked', new Date().toISOString());
    })
    .catch(err => {
      // If there was an error, we might want to allow trying again
      if (!err.response || err.response.status >= 500) {
        localStorage.removeItem('hasTrackedReferral');
      }
    })
    .finally(() => {
      // Reset tracking flag regardless of success/failure
      clickTrackingInProgress = false;
    });
};

// Store referral code in localStorage with 30-day expiration
export const storeReferralCode = (code) => {
  if (!code) {
    console.error('Attempted to store empty referral code');
    return;
  }
  
  // Check if already stored to prevent multiple storage operations
  const existingCode = localStorage.getItem('referralCode');
  if (existingCode === code) {
    return;
  }
  
  // Calculate expiration date (30 days from now)
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 30);
  
  // Store code and expiration
  localStorage.setItem('referralCode', code);
  localStorage.setItem('referralCodeExpiry', expirationDate.toISOString());
};

// Get stored referral code if not expired
export const getStoredReferralCode = () => {
  const code = localStorage.getItem('referralCode');
  const expiryString = localStorage.getItem('referralCodeExpiry');
  
  if (!code || !expiryString) {
    return null;
  }
  
  // Check if expired
  const expiryDate = new Date(expiryString);
  const now = new Date();
  
  if (now > expiryDate) {
    // Clear expired referral data
    localStorage.removeItem('referralCode');
    localStorage.removeItem('referralCodeExpiry');
    localStorage.removeItem('referralCodeLastTracked');
    localStorage.removeItem('hasTrackedReferral');
    return null;
  }
  
  return code;
};

// Track referral link click via API call
export const trackReferralClick = async (referralCode, clickId = CLICK_ID) => {
  if (!referralCode) {
    console.error('No referral code provided for tracking');
    return;
  }
  
  try {
    const response = await axios.post(`${BACKEND_API_URL}/api/auth/user/affiliate/click`, {
      referralCode,
      clickId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Reset tracking state - useful for testing or forcing a new tracking
export const resetTrackingState = () => {
  sessionStorage.removeItem('hasTrackedReferral');
  localStorage.removeItem('hasTrackedReferral');
  clickTrackingInProgress = false;
};

// Component to be placed at the top level of the app
// Only captures referral on first render and passes in login state
const ReferralTracker = ({ children, userLoggedIn = false }) => {
  useEffect(() => {
    // Check URL for referral code only on initial mount
    // Pass the login state to determine whether to track clicks
    captureReferralCode(userLoggedIn);
  }, [userLoggedIn]);

  // Render children without adding any DOM elements
  return <>{children}</>;
};

export default ReferralTracker; 